import { CategoryTab } from 'app/design/categories/tab';
import type { ButtonProps } from 'app/design/primitives/button';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { withAnimated } from 'app/hooks/animation';
import type { Category } from 'app/types/category';
import { View } from 'react-native';
import { FadeInUp, FadeOutDown } from 'react-native-reanimated';
import { getI18nLanguage } from 'app/lib/language';

const AnimatedButton = withAnimated(Button);

interface CategoryButtonProps extends Omit<ButtonProps, 'children'> {
  category: Category;
  categoryNameSubstitute?: string;
  showIcon?: boolean;
  rightAlignIcon?: boolean;
  centerText?: boolean;
  roundButton?: boolean;
}

function getCategoryButtonPresentation({
  category,
  centerText,
  rightAlignIcon,
  roundButton,
  showIcon,
  variant,
}: Pick<CategoryButtonProps, 'category' | 'centerText' | 'rightAlignIcon' | 'roundButton' | 'showIcon'> & {
  variant: ButtonProps['variant'];
}) {
  const isPill = variant === 'category' || variant === 'small-pill';
  const isSmall = variant === 'small-pill';
  const foregroundColor = isPill ? category.colorDark : 'black';
  const textStyle = {
    color: foregroundColor,
    ...(!isPill
      ? {
          marginRight: showIcon ? -12 : 0,
          fontSize: showIcon ? 13 : 14,
          textAlign: centerText ? ('center' as const) : undefined,
        }
      : {}),
    ...(isSmall ? { fontSize: 12 } : {}),
  };

  return {
    foregroundColor,
    iconMarginLeft: isPill ? 0 : -10,
    iconSize: isSmall ? 18 : 20,
    rootFlex: isSmall ? 0 : 1,
    rootMinWidth: isSmall ? undefined : 88,
    rootStyle: {
      borderWidth: isPill ? 0 : 2,
      borderColor: category.colorMedium,
      ...(roundButton ? { borderRadius: 20 } : {}),
      backgroundColor: isPill ? category.colorLight : undefined,
    },
    textFlex: !isPill && showIcon && rightAlignIcon ? 1 : undefined,
    textStyle,
  };
}

export function CategoryButton({
  category,
  categoryNameSubstitute,
  showIcon = false,
  rightAlignIcon = false,
  centerText = false,
  roundButton = false,
  ...rest
}: CategoryButtonProps) {
  const i18nLanguage = getI18nLanguage();
  const presentation = getCategoryButtonPresentation({
    category,
    centerText,
    rightAlignIcon,
    roundButton,
    showIcon,
    variant: rest.variant,
  });

  return (
    <AnimatedButton
      entering={FadeInUp}
      exiting={FadeOutDown}
      flex={presentation.rootFlex}
      minWidth={presentation.rootMinWidth}
      style={presentation.rootStyle}
      variant="category"
      {...rest}
    >
      {showIcon ? (
        <View style={{ marginLeft: presentation.iconMarginLeft }}>
          <CategoryTab category={category.icon} color={presentation.foregroundColor} size={presentation.iconSize} />
        </View>
      ) : null}
      <Text
        adjustsFontSizeToFit
        flex={presentation.textFlex}
        fontWeight="700"
        numberOfLines={1}
        style={presentation.textStyle}
        variant="bodyBase"
      >
        {categoryNameSubstitute ?? category.name[i18nLanguage]}
      </Text>
    </AnimatedButton>
  );
}
